import * as baseConfig from '@/config/base-config.json';
import * as config from '@/config/config.json';
import { FinancingLoanType } from '../common';
import { generateSliderSteps } from '@/utils/generateSliderSteps';

export const HDB_RESALE_PURCHASE_LABEL = {
  PurchaseDetail: 'Purchase Details',
  PurchasePrice: 'Purchase Price',
  ValuationPrice: 'Valuation Price',
  COVLongForm: 'Cash Over Valuation',
  COV: 'COV',
  CpfAndGrants: 'CPF & Grants',
  TotalCpfOaAvailable: 'Total CPF (OA) Available',
  CpfHousingGrant: 'CPF Housing Grant (First Timer)',
  EnhancedCpfHousingGrant: 'Enhanced CPF Housing Grant (EHG)',
  ProximityHousingGrant: 'Proximity Housing Grant (PHG)',
  TotalCpfAndGrantsAvailable: 'Total CPF & Grants Available',
  PotentialExpenses: 'Potential Expenses',
  OptionFee: 'Option Fee',
  OptionExerciseFee: 'Option Exercise Fee',
  BSDLongForm: "Buyer's Stamp Duty",
  BSD: 'BSD',
  ABSDLongForm: "Additional Buyer's Stamp Duty",
  ABSD: 'ABSD',
  LegalFee: 'Legal Fee',
  ResaleApplicationFee: 'Resale Application Fee',
  ValuationFee: 'Valuation Fee',
  ServiceCommission: 'Service Commission',
  Others: 'Others',
  HousingLoan: 'Housing Loan',
  BankLoan: 'Bank Loan',
  HdbLoan: 'HDB Loan',
  NoLoan: 'No Loan',
  FinancingVariables: 'Financing Variables',
  LoanTenure: 'Loan Tenure (Years)',
  MortgageLoanInterestRate: 'Mortgage Loan Interest Rate',
  LoanToValue: 'Loan-to-Value (LTV)',
  BreakdownOfValuationPrice: 'Breakdown of Valuation Price',
  Valuation: 'Valuation',
  Cash: 'Cash',
  CpfOaPerCash: 'CPF OA / Cash',
  LoanAmount: 'Loan Amount',
  MonthlyPayable: 'Monthly Payable',
  BothCpfAndCashUsable: 'Both CPF & Cash usable',
  Total: 'Total',
  Principal: 'Principal',
  Interest: 'Interest',
  MonthlyPayableNote: 'Estimation for the first payment month only',
  CashBreakdown: 'Cash Breakdown',
  MandatoryCashTowardsValuation: 'Mandatory Cash Towards Valuation',
  TotalCashOutlay: 'Total Cash Outlay',
  DownpaymentBreakdown: 'Downpayment Breakdown',
  DepositToBePaid: 'Deposit to be paid',
  OutstandingPayablesInCash: 'Outstanding Payables in Cash',
  LessDeposit: 'Less Deposit',
  OutstandingPayablesInCpfOaPerCash: 'Outstanding Payables in CPF OA / Cash',
  TotalPurchaseOutlay: 'Total Purchase Outlay',
  Summary: 'Summary',
  AmountPayableInCpfIfSufficient: 'Amount Payable in CPF, if Sufficient',
  TotalCashRequired: 'Total Cash Required',
  PurchaseTimeline: 'Purchase Timeline',
  OTPLongForm: 'Option to Purchase',
  OTP: 'OTP',
  ExercisePeriod: 'Exercise Period',
  SubmitOfResaleApplication: 'Submit of Resale Application',
  AcceptanceOfResaleApplication: 'Acceptance of Resale Application',
  TemporaryExtensionOFStay: 'Temporary Extension of Stay',
  Days: 'Days',
  WorkingDays: 'Working Days',
  Months: 'Months',
  OtpGranted: 'OTP Granted',
  ExerciseOfOTP: 'Exercise of OTP',
  ExerciseFee: 'Exercise Fee',
  EstimatedHdbApprovalLetter: 'Estimated HDB Approval Letter',
  HdbResaleCompletion: 'HDB Resale Completion',
  AfterCpfAndLoan: 'After CPF & Loan',
  AgentFee: 'Agent Fee',
  Amount: 'Amount',
  ActualHandover: 'Actual Handover',
  GenerateTimeline: 'Generate Timeline',
};

export const Deposit = baseConfig.deposit_in_cash.max;

export const OptionFee = {
  min: baseConfig.option_fee_cash.min,
  max: baseConfig.option_fee_cash.max,
};

export const OptionExerciseFee = {
  min: baseConfig.option_exercise_fee_cash.min,
  max: baseConfig.option_exercise_fee_cash.max,
};

export enum HousingLoanType {
  BANK_LOAN = 'Bank Loan',
  HDB_LOAN = 'HDB Loan',
  NO_LOAN = 'No Loan',
}

export const DEFAULT_LOAN_DETAILS_AGE_65 = {
  AGE: FinancingLoanType.sixtyFiveYearLoan,
  LOAN_TENURE: config.max_loan_tenure_in_years.hdb.bank_loan.upto_65_financing_year,
  LOAN_TENURE_LABEL: generateSliderSteps(
    0,
    config.max_loan_tenure_in_years.hdb.bank_loan.upto_65_financing_year,
    10,
  ),
  LOAN_TO_VALUE_MAX: config.max_ltv_in_percent.hdb.upto_65_financing_year,
  LOAN_TO_VALUE_DEFAULT: config.max_ltv_in_percent.hdb.upto_65_financing_year,
  LOAN_TO_VALUE_LABEL: generateSliderSteps(
    0,
    config.max_ltv_in_percent.hdb.upto_65_financing_year,
    15,
  ),
};

export const DEFAULT_LOAN_DETAILS_AGE_75 = {
  AGE: FinancingLoanType.seventyFiveYearLoan,
  LOAN_TENURE: config.max_loan_tenure_in_years.hdb.bank_loan.upto_75_financing_year,
  LOAN_TENURE_LABEL: generateSliderSteps(
    0,
    config.max_loan_tenure_in_years.hdb.bank_loan.upto_75_financing_year,
    10,
  ),
  LOAN_TO_VALUE_MAX: config.max_ltv_in_percent.hdb.upto_75_financing_year,
  LOAN_TO_VALUE_DEFAULT: config.max_ltv_in_percent.hdb.upto_75_financing_year,
  LOAN_TO_VALUE_LABEL: generateSliderSteps(
    0,
    config.max_ltv_in_percent.hdb.upto_75_financing_year,
    15,
  ),
};

export const HDB_LOAN_MORTGAGE_INTEREST_RATE = baseConfig.mortgage_interest_rate.hdb.hdb_loan;

export const MORTGAGE_INTEREST_RATE = {
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.mortgage_interest_rate.hdb.min,
    baseConfig.mortgage_interest_rate.hdb.max,
    1,
  ),
  DEFAULT: baseConfig.mortgage_interest_rate.hdb.default,
  MAX: baseConfig.mortgage_interest_rate.hdb.max,
  MIN: baseConfig.mortgage_interest_rate.hdb.min,
  STEP: 0.01,
};

export const CASH = {
  FIVE_PERCENT: config.min_cash_downpayment_in_percent.hdb.upto_65_financing_year,
  TEN_PERCENT: config.min_cash_downpayment_in_percent.hdb.upto_75_financing_year,
};

export const BREAKDOWN_LABELS = [
  HDB_RESALE_PURCHASE_LABEL.Cash,
  HDB_RESALE_PURCHASE_LABEL.CpfOaPerCash,
  HDB_RESALE_PURCHASE_LABEL.LoanAmount,
];
export const BREAKDOWN_COLORS = ['#F8CB59', '#9475EB', '#F25A73'];

export const MONTHLY_LABELS = [
  HDB_RESALE_PURCHASE_LABEL.Principal,
  HDB_RESALE_PURCHASE_LABEL.Interest,
];
export const MONTHLY_COLORS = ['#9475EB', '#F25A73'];

// Timeline Configuration
export const HDB_RESALE_COMPLETION_PERIOD_IN_DAYS = baseConfig.hdb_resale_completion_period_in_days;
export const HDB_APPROVAL_LETTER_PERIOD_IN_DAYS = baseConfig.hdb_approval_letter_period_in_days;
export const TEMPORARY_EXTENSION_START_DELAY_IN_DAYS =
  baseConfig.temporary_extension_start_delay_in_days;

export const EXERCISE_PERIOD = {
  DEFAULT: baseConfig.exercise_period_in_days.default,
  MIN: baseConfig.exercise_period_in_days.min,
  MAX: baseConfig.exercise_period_in_days.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.exercise_period_in_days.min,
    baseConfig.exercise_period_in_days.max,
  ),
};

export const COMPLETION_PERIOD = {
  DEFAULT: baseConfig.completion_period_in_weeks.default,
  MIN: baseConfig.completion_period_in_weeks.min,
  MAX: baseConfig.completion_period_in_weeks.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.completion_period_in_weeks.min,
    baseConfig.completion_period_in_weeks.max,
  ),
};

export const SUBMIT_RESALE_APPLICATION = {
  DEFAULT: baseConfig.submit_resale_application_in_days.default,
  MIN: baseConfig.submit_resale_application_in_days.min,
  MAX: baseConfig.submit_resale_application_in_days.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.submit_resale_application_in_days.min,
    baseConfig.submit_resale_application_in_days.max,
  ),
};

export const ACCEPTANCE_RESALE_APPLICATION = {
  DEFAULT: baseConfig.acceptance_resale_application_in_days.default,
  MIN: baseConfig.acceptance_resale_application_in_days.min,
  MAX: baseConfig.acceptance_resale_application_in_days.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.acceptance_resale_application_in_days.min,
    baseConfig.acceptance_resale_application_in_days.max,
  ),
};

export const TEMPORARY_EXTENSION_OF_STAY = {
  DEFAULT: baseConfig.temporary_extension_stay_in_months.default,
  MIN: baseConfig.temporary_extension_stay_in_months.min,
  MAX: baseConfig.temporary_extension_stay_in_months.max,
  STEP: 0.5,
  LABEL_INPUTS: generateSliderSteps(
    baseConfig.temporary_extension_stay_in_months.min,
    baseConfig.temporary_extension_stay_in_months.max,
    0.5,
  ),
};
